import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { ApprovalDetailsTableData } from './useApprovalDetailsTableData';
import { Chip, useTheme } from '@mui/material';
import { Typography } from '../../../components/Typography/Typography';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { format, parseISO } from 'date-fns';
import { TimesheetLogType } from '../../../types';
import { roleLabel } from '../../../utils/roleLabels';

const columnHelper = createColumnHelper<ApprovalDetailsTableData>();

export const useApprovalDetailsModalColumns = () => {
  const { colors } = useTheme();

  const getChipColor = useCallback(
    (status: TimesheetLogType) => {
      switch (status) {
        case TimesheetLogType.REJECTED:
          return colors.textStatus.error;
        case TimesheetLogType.SUBMITTED:
        case TimesheetLogType.APPROVED:
          return colors.textStatus.success;
        case TimesheetLogType.NOT_REQUIRED:
          return colors.textStatus.noAction;
        default:
          return colors.textStatus.active;
      }
    },
    [
      colors.textStatus.success,
      colors.textStatus.error,
      colors.textStatus.noAction,
      colors.textStatus.active,
    ]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('role', {
        cell: (info) => (
          <Typography variant='h4' color={colors.text.main}>
            {roleLabel(info.getValue())}
          </Typography>
        ),
        header: () => (
          <TableHeaderCell
            text='Role'
            style={{
              color: colors.text.caption,
            }}
          />
        ),
        meta: {
          width: '170px',
          maxWidth: '170px',
          minWidth: '170px',
          thStyles: { paddingLeft: '16px', border: 'none' },
          tdStyles: { paddingLeft: '16px', border: 'none' },
        },
      }),
      columnHelper.accessor('approver', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.main}>
            {info.getValue()}
          </Typography>
        ),
        header: () => (
          <TableHeaderCell
            text='Approver'
            style={{
              color: colors.text.caption,
            }}
          />
        ),
        meta: {
          width: '205px',
          maxWidth: '205px',
          minWidth: '205px',
          thStyles: { paddingLeft: '16px', border: 'none' },
          tdStyles: { paddingLeft: '16px', border: 'none' },
        },
      }),
      columnHelper.accessor('dateStart', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.main}>
            {info.getValue() ? format(parseISO(info.getValue() ?? ''), 'MMM d, yyyy') : '-'}
          </Typography>
        ),
        header: () => (
          <TableHeaderCell
            text='Date Start'
            style={{
              color: colors.text.caption,
            }}
          />
        ),
        meta: {
          width: '130px',
          maxWidth: '130px',
          minWidth: '130px',
          thStyles: { paddingLeft: '16px', border: 'none' },
          tdStyles: { paddingLeft: '16px', border: 'none' },
        },
      }),
      columnHelper.accessor('dateEnd', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.main}>
            {info.getValue() ? format(parseISO(info.getValue() ?? ''), 'MMM d, yyyy') : '-'}
          </Typography>
        ),
        header: () => (
          <TableHeaderCell
            text='Date End'
            style={{
              color: colors.text.caption,
            }}
          />
        ),
        meta: {
          width: '130px',
          maxWidth: '130px',
          minWidth: '130px',
          thStyles: { paddingLeft: '16px', border: 'none' },
          tdStyles: { paddingLeft: '16px', border: 'none' },
        },
      }),
      columnHelper.accessor('daysPending', {
        cell: (info) => (
          <Typography
            variant='body'
            color={colors.text.main}
            style={{
              textAlign: 'right',
              paddingRight: '8px',
            }}
          >
            {info.getValue()}
          </Typography>
        ),
        header: () => (
          <TableHeaderCell
            text='Days Pending'
            style={{
              color: colors.text.caption,
            }}
          />
        ),
        meta: {
          width: '130px',
          maxWidth: '130px',
          minWidth: '130px',
          thStyles: { paddingLeft: '16px', border: 'none' },
          tdStyles: { paddingLeft: '16px', border: 'none' },
        },
      }),
      columnHelper.accessor('status', {
        cell: (info) => (
          <Chip
            style={{
              backgroundColor: colors.surfaceBackground.bg4,
              minWidth: '66px',
              height: '28px',
            }}
            label={
              <Typography variant='body' color={getChipColor(info.getValue())}>
                {info.getValue()}
              </Typography>
            }
          />
        ),
        header: () => (
          <TableHeaderCell
            text='Status'
            style={{
              color: colors.text.caption,
            }}
          />
        ),
        meta: {
          thStyles: { paddingLeft: '16px', border: 'none' },
          tdStyles: { paddingLeft: '16px', border: 'none' },
        },
      }),
    ],
    [getChipColor, colors.surfaceBackground.bg4, colors.text.main, colors.text.caption]
  );
  return columns;
};
